import { api } from "../client";
import { endpoints } from "../endpoints";
import { validateSession } from "../../auth/session";

function loginResult(data) {
  const needsOtp = data?.otp_required === true;
  const needsSetup = data?.otp_setup_required === true;
  if ((needsOtp && needsSetup) || ((needsOtp || needsSetup) && data?.token)) {
    throw new Error("پاسخ ورود ناسازگار است. لطفاً با پشتیبانی تماس بگیرید.");
  }
  if (needsSetup) return { status: "otp_setup_required" };
  if (needsOtp) return { status: "otp_required" };
  return { status: "authenticated", session: validateSession(data) };
}

function credentials({ username, password, otp_token }) {
  const data = { username, password };
  if (otp_token !== undefined) data.otp_token = otp_token;
  return data;
}

// Authentication actions never opt into automatic retries.
export function createAuthService(client) {
  const send = async (url, options) => {
    const response = await client.request({ url, ...options });
    return response.data;
  };

  return {
    async login(input, { signal } = {}) {
      const data = await send(endpoints.auth.login, {
        method: "POST", auth: false, data: credentials(input), signal,
      });
      return loginResult(data);
    },

    async setup({ username, password }, { signal } = {}) {
      const data = await send(endpoints.auth.setup, {
        method: "POST", auth: false, data: { username, password }, signal,
      });
      let uri;
      try {
        uri = new URL(data?.otpauth_url);
      } catch {
        throw new Error("اطلاعات راه‌اندازی ورود دومرحله‌ای معتبر نیست.");
      }
      if (
        uri.protocol !== "otpauth:" || uri.hostname !== "totp" ||
        typeof data?.secret !== "string" || !data.secret.trim()
      ) {
        throw new Error("اطلاعات راه‌اندازی ورود دومرحله‌ای معتبر نیست.");
      }
      return { otpauth_url: data.otpauth_url, secret: data.secret };
    },

    async confirm(input, { signal } = {}) {
      const data = await send(endpoints.auth.confirm, {
        method: "POST", auth: false, data: credentials(input), signal,
      });
      const result = loginResult(data);
      if (result.status !== "authenticated") {
        throw new Error("تأیید ورود دومرحله‌ای کامل نشد.");
      }
      return result;
    },

    status({ signal } = {}) {
      return send(endpoints.auth.status, { safeRead: true, signal });
    },

    // The checklist documents row fields, but not the list envelope.
    sessions({ signal } = {}) {
      return send(endpoints.auth.sessions, { safeRead: true, signal });
    },

    revokeSession(id, { signal } = {}) {
      if (id === null || id === undefined || id === "") {
        throw new Error("این نشست شناسه قابل لغو ندارد.");
      }
      return send(endpoints.auth.session(id), { method: "DELETE", signal });
    },

    logout({ signal } = {}) {
      return send(endpoints.auth.logout, { method: "POST", signal });
    },

    logoutAll({ signal } = {}) {
      return send(endpoints.auth.logoutAll, { method: "POST", signal });
    },
  };
}

export function loginErrorMessage(error, stage = "credentials") {
  if (typeof error.data?.detail === "string" && error.data.detail) {
    return error.data.detail;
  }
  if (error.status === 429) return "درخواست‌های زیادی ارسال شده است. کمی صبر کنید.";
  if (error.status === 403) return "ورود موقتاً مسدود شده است. بعداً دوباره تلاش کنید.";
  if (error.status === 400 || error.status === 401) {
    return stage === "credentials"
      ? "نام کاربری یا رمز عبور نادرست است."
      : "کد ورود دومرحله‌ای نادرست است.";
  }
  return error.message;
}

export const authService = createAuthService(api);
